"use client";

import { Core } from "@faire/design-tokens";
import { optimizeImageUrl } from "@faire/web--source/common/images/optimizeImages";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { Row } from "@faire/web--source/slate/Layout";
import { Typography } from "@faire/web--source/slate/Typography";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import { observer } from "mobx-react";
import React, { useState } from "react";
import styled, { css, keyframes } from "styled-components";

const INTRO_FULL_IMAGE_WITH_OVERLAY_DURATION = 1000;
const IMAGE_DELAY_DURATION = 2000;
const DISSOLVE_DURATION = 500;
const SINGLE_IMAGE_SHOWING_TIME = IMAGE_DELAY_DURATION + DISSOLVE_DURATION;
const INTRO_DURATION =
  IMAGE_DELAY_DURATION +
  INTRO_FULL_IMAGE_WITH_OVERLAY_DURATION +
  DISSOLVE_DURATION;

interface IProps {
  images?: IImage[];
}

const ShowroomAnimatedCategoryTileBase: React.FC<IProps> = ({ images }) => {
  const isSSR = useIsSSR();
  const [isImageLoading, setIsImageLoading] = useState(true);

  const onImageLoading = () => {
    setIsImageLoading(false);
  };

  const totalDuration = !images?.length
    ? 0
    : SINGLE_IMAGE_SHOWING_TIME * (images.length - 1);
  const fadeInPercent = `${(100 * DISSOLVE_DURATION) / totalDuration}%`;
  const delayPercent = `${(100 * SINGLE_IMAGE_SHOWING_TIME) / totalDuration}%`;
  const fadeOutPercent = `${
    (100 * (SINGLE_IMAGE_SHOWING_TIME + DISSOLVE_DURATION)) / totalDuration
  }%`;

  const [fullImage, ...thinImages] = images ?? [];

  return (
    <ImageWrapper>
      {fullImage ? (
        <FullSizeImage
          src={optimizeImageUrl(fullImage, isSSR)}
          $startAnimation={!isImageLoading}
          $shouldFadeOut={(images?.length ?? 0) > 1}
          onLoad={onImageLoading}
        />
      ) : null}
      {thinImages.map((image, index) => (
        <ThinImage
          src={optimizeImageUrl(image, isSSR)}
          $startAnimation={!isImageLoading}
          $delayedStart={INTRO_DURATION + index * SINGLE_IMAGE_SHOWING_TIME}
          key={index}
          $fadeInPercent={fadeInPercent}
          $fadeOutPercent={fadeOutPercent}
          $delayPercent={delayPercent}
          $total={totalDuration}
          style={{ width: "100%", height: "100%" }}
          width="100%"
          height="100%"
        />
      ))}

      <Overlay $startAnimation={!isImageLoading} />
      <TextWrapper $startAnimation={!isImageLoading}>
        <StyledTypography
          color={Core.text.inverse}
          variant="subheadingSansMedium"
          $startAnimation={!isImageLoading}
        >
          {/* this following string does not need translation, if the string get translated, the animation might be broken */}
          SHOW
          <br />
          ROOM
        </StyledTypography>
      </TextWrapper>
    </ImageWrapper>
  );
};

export const ShowroomAnimatedCategoryTile = observer(
  ShowroomAnimatedCategoryTileBase
);

const overlayAnimation = keyframes`
  0% {
    opacity: 0.8;
    transform: translateX(0);
  }
  100% {
    opacity: 1;
    transform: translateX(-50%);
  }
`;

const textAnimation = keyframes`
  0% {
    transform: scale(1);
  }
  100% {
    transform: scale(0.7);
  }
`;

const textWrapperAnimation = keyframes`
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-25%);
  }
`;

const fullSizeImageFadeInAnimation = keyframes`
  0% {
  transform: scale(1.1) translateX(0);
  }
  100% {
    transform: scaleX(0.95) translateX(30%);
  }
`;

const fullSizeImageFadeOutAnimation = keyframes`
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
`;

const thinImageAnimation = (fadeIn: string, delay: string, fadeOut: string) =>
  keyframes`
  0% {
    opacity: 0;
  }
  ${fadeIn},${delay} {
    opacity: 1;
  }
  ${fadeOut}, 100%  {
    opacity: 0;
  }
`;

const Overlay = styled(Row).attrs({ align: "center", justify: "center" })<{
  $startAnimation: boolean;
}>`
  background-color: ${Core.surface.enabled};
  width: 100%;
  opacity: 0.8;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  ${(props) =>
    props.$startAnimation
      ? css`
          animation: ${overlayAnimation} ${DISSOLVE_DURATION}ms ease-in
            ${INTRO_FULL_IMAGE_WITH_OVERLAY_DURATION}ms 1 forwards;
        `
      : ""}
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 0;
  overflow: hidden;
  padding-top: calc(100% / 0.75);
`;

const FullSizeImage = styled.img<{
  $startAnimation: boolean;
  $shouldFadeOut: boolean;
}>`
  width: 100%;
  transform: scale(1.1);
  object-fit: contain;
  position: absolute;
  top: 0;
  left: 0;
  ${(props) =>
    props.$startAnimation && props.$shouldFadeOut
      ? css`
          animation:
            ${fullSizeImageFadeInAnimation} ${DISSOLVE_DURATION}ms ease-in
              ${INTRO_FULL_IMAGE_WITH_OVERLAY_DURATION}ms 1 forwards,
            ${fullSizeImageFadeOutAnimation} ${DISSOLVE_DURATION}ms ease-in
              ${INTRO_DURATION}ms 1 forwards;
        `
      : ""}
  ${(props) =>
    props.$startAnimation && !props.$shouldFadeOut
      ? css`
          animation: ${fullSizeImageFadeInAnimation} ${DISSOLVE_DURATION}ms
            ease-in ${INTRO_FULL_IMAGE_WITH_OVERLAY_DURATION}ms 1 forwards;
        `
      : ""}
`;

const StyledTypography = styled(Typography)<{
  $startAnimation: boolean;
}>`
  &&& {
    font-size: 20px;
    transform-origin: center;
    ${(props) =>
      props.$startAnimation
        ? css`
            animation: ${textAnimation} ${DISSOLVE_DURATION}ms ease-in
              ${INTRO_FULL_IMAGE_WITH_OVERLAY_DURATION}ms 1 forwards;
          `
        : ""}
  }
`;

const TextWrapper = styled(Row).attrs({ align: "center", justify: "center" })<{
  $startAnimation: boolean;
}>`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  ${(props) =>
    props.$startAnimation
      ? css`
          animation: ${textWrapperAnimation} ${DISSOLVE_DURATION}ms ease-in
            ${INTRO_FULL_IMAGE_WITH_OVERLAY_DURATION}ms 1 forwards;
        `
      : ""}
`;

const ThinImage = styled.img<{
  $delayedStart: number;
  $fadeInPercent: string;
  $delayPercent: string;
  $fadeOutPercent: string;
  $total: number;
  $startAnimation: boolean;
}>`
  width: 50%;
  height: 100%;
  opacity: 0;
  position: absolute;
  top: 0;
  right: 0;
  ${(props) =>
    props.$startAnimation
      ? css`
          animation: ${thinImageAnimation(
              props.$fadeInPercent,
              props.$delayPercent,
              props.$fadeOutPercent
            )}
            ${props.$total}ms ease-in-out ${props.$delayedStart}ms infinite
            forwards;
        `
      : ""}
`;
